import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useProfile } from '../contexts/ProfileContext';
import { useAuth } from '../contexts/AuthContext';
import { StartupProfile as StartupProfileType } from '../types/firestore';
import StartupProfileComponent from '../components/profile/StartupProfile';
import ProfileWizard from '../components/profile/ProfileWizard';
import ProfileReadyPopup from '../components/profile/ProfileReadyPopup';
import RateRegistrationPopup from '../components/profile/RateRegistrationPopup';
import PauseProfilePage from './PauseProfilePage';
import writeIcon from '../assets/write.png';

function withSlightlyBoldCurrently(label: string) {
  const [before, after] = label.split('currently');
  if (after === undefined) return label;
  return (
    <>
      {before}
      <span style={{ fontWeight: 500 }}>currently</span>
      {after}
    </>
  );
}

function getStatusBadge(profile: StartupProfileType): { color: string; label: string } {
  if (profile.paused) {
    return { color: '#ca8a04', label: 'Your profile is currently paused' };
  }
  return { color: '#598c21', label: 'You are currently seeking funding' };
}

const StartupProfileViewPage: React.FC = () => {
  const { startupProfile, loading, profileLoadAttempted } = useProfile();
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isEditing, setIsEditing] = useState(false);
  const [isChangingStatus, setIsChangingStatus] = useState(false);
  const [showRatePopup, setShowRatePopup] = useState(false);
  const [showProfileReadyPopup, setShowProfileReadyPopup] = useState(false);
  /** Captured when opening the editor — context may flip profileCompleted before onSave. */
  const editingFromIncompleteRef = React.useRef(false);

  useEffect(() => {
    setIsEditing(false);
    setIsChangingStatus(false);
  }, [location.pathname]);

  /** Notion: SUCCESS + rating first, then “how investors see you” on the profile. */
  const openFirstCompletionPopups = () => {
    if (user?.userType === 'startup' && !user.rating) {
      setShowRatePopup(true);
      return;
    }
    setShowProfileReadyPopup(true);
  };

  const startEditing = () => {
    editingFromIncompleteRef.current = !startupProfile?.profileCompleted;
    setIsChangingStatus(false);
    setIsEditing(true);
  };

  const closeEditor = () => {
    const firstCompletion = editingFromIncompleteRef.current;
    editingFromIncompleteRef.current = false;
    setIsEditing(false);
    if (firstCompletion) openFirstCompletionPopups();
  };

  useEffect(() => {
    const state = location.state as { showProfileReadyPopup?: boolean } | null;
    if (!state?.showProfileReadyPopup) return;
    openFirstCompletionPopups();
    navigate(location.pathname, { replace: true, state: null });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only when nav state asks for the popup
  }, [location.pathname, location.state, navigate]);

  if (loading || !profileLoadAttempted) {
    return (
      <div className="min-h-screen bg-[#1a1a1a] flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-emerald-500 mb-4" />
          <p className="text-white text-lg">Loading profile...</p>
        </div>
      </div>
    );
  }

  if (!startupProfile) {
    return (
      <div className="min-h-screen bg-[#1a1a1a] flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-400 text-lg mb-4">Profile not found</p>
          <button
            onClick={() => navigate('/profile/startup')}
            className="px-4 py-2 bg-emerald-700 text-white rounded hover:bg-emerald-800"
          >
            Complete your profile
          </button>
        </div>
      </div>
    );
  }

  const { color: badgeColor, label: badgeLabel } = getStatusBadge(startupProfile);

  // Edit and status change sit in the same pitch column as the dashboard profile.
  // View mode leaves rightPanel empty so the shared card, pitch pane, and black bars render.
  const rightPanel =
    isEditing || isChangingStatus ? (
      <div className="hidden lg:flex lg:min-w-0 lg:flex-1 lg:flex-col lg:sticky lg:top-[6rem] lg:mt-0 lg:min-h-0 lg:h-full">
        <div className="flex h-[calc(100vh_-_6rem_-_160px)] min-h-0 flex-col bg-[#1d1d1d] px-2 pt-0 pb-0">
          <div className="relative h-full min-h-0 flex-1 overflow-hidden bg-[#303030] p-2">
            {isEditing ? (
              <div className="absolute inset-0 z-10 overflow-y-auto bg-[#1d1d1d] scrollbar-visible-dark">
                <ProfileWizard userType="startup" embedded onSave={closeEditor} />
              </div>
            ) : (
              <div className="absolute inset-0 z-10 overflow-y-auto bg-[#1d1d1d] scrollbar-visible-dark">
                <PauseProfilePage onDone={() => setIsChangingStatus(false)} />
              </div>
            )}
          </div>
        </div>
      </div>
    ) : undefined;

  const statusBadge = (
    <div className="pointer-events-auto absolute inset-x-0 top-0 z-20 flex -translate-y-1/2 justify-center px-2">
      <div
        className="inline-flex items-center gap-2 px-4 py-1.5 rounded-b-2xl text-sm !font-normal tracking-normal border-b border-x border-gray-800 shadow-[0_8px_20px_rgba(0,0,0,0.22)] whitespace-nowrap"
        style={{
          backgroundColor: badgeColor,
          fontFamily: 'Rubik, sans-serif',
          fontWeight: 400,
        }}
      >
        <span style={{ color: startupProfile.paused ? '#ffffff' : '#111111' }}>
          {withSlightlyBoldCurrently(badgeLabel)}
        </span>
        <button
          onClick={() => {
            editingFromIncompleteRef.current = false;
            setIsChangingStatus(true);
            setIsEditing(false);
          }}
          className="font-normal underline underline-offset-2"
          style={{ color: '#444444', fontFamily: 'Rubik, sans-serif', fontWeight: 400 }}
        >
          Edit
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen overflow-x-hidden bg-[#1d1d1d]">
      <div className="pt-4">
        <StartupProfileComponent
          profile={startupProfile}
          hideActions={true}
          compactDeck={true}
          leftPanelHeader={statusBadge}
          rightPanel={rightPanel}
        />
      </div>

      {!isEditing && !isChangingStatus && (
        <div className="flex justify-center py-6">
          <button
            onClick={startEditing}
            className="flex items-center gap-2 text-gray-400 italic hover:text-white transition-colors text-sm underline"
          >
            <img src={writeIcon} alt="" className="w-4 h-4 opacity-60" />
            Edit / update your profile
          </button>
        </div>
      )}

      {/* Mobile: wizard rendered below the profile when editing */}
      {isEditing && (
        <div className="lg:hidden">
          <ProfileWizard userType="startup" embedded onSave={closeEditor} />
        </div>
      )}

      {/* Mobile: pause/status page rendered below the profile when changing status */}
      {isChangingStatus && (
        <div className="lg:hidden">
          <PauseProfilePage onDone={() => setIsChangingStatus(false)} />
        </div>
      )}

      <RateRegistrationPopup
        isOpen={showRatePopup}
        onClose={() => {
          setShowRatePopup(false);
          // After SUCCESS + rating, show how investors see the profile (light overlay).
          setShowProfileReadyPopup(true);
        }}
      />
      <ProfileReadyPopup
        isOpen={showProfileReadyPopup}
        onClose={() => setShowProfileReadyPopup(false)}
      />
    </div>
  );
};

export default StartupProfileViewPage;
